import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
/** «Статус · все филиалы» — тепловая карта карточек (Алексей 24.09, AI183: «тут сам реши»).
 *  Те же панели, что на странице филиала: четыре — по выжимке снимков (без фото), приход /
 *  речь / отзывы / визиты — по тем же ручкам, что у филиала (#2279: «в таблице не все параметры»);
 *  вся строка — по худшему, сортировка по «цене» проблем. Динамика — против предыдущих 14 дней. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useBranchScope } from '@/hooks/useBranchScope';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, BarChart3, ChevronDown, ChevronUp, ChevronsUpDown, Settings2 } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { getStaffArrival, getStatusAll, type StatusAllZone } from '@/api/analytics';
import { getZoneFeatures } from '@/api/features';
import { getConversationsOverview } from '@/api/conversations';
import { getReviewsStats } from '@/api/reviews';
import { listZoneVisits } from '@/api/visits';
import { getHoursChecks } from '@/api/brands';
import { getReceiptsSummary, getStatusWeights, putStatusWeights } from '@/api/receipts';
import { useZoneFeatures } from '@/hooks/useZoneFeatures';
import AnalyticsStatusTab from './AnalyticsStatusTab';
import {
  aggArrival, aggHours, aggLayout, aggQueue, aggReceipts, aggReturns, aggReviews, aggSpeech, aggTables, aggTasks, aggVisits, DayStrip, isoDaysAgo, LevelChip, LIGHT_CHIP, LIGHT_DOT,
  LIGHT_LABEL, LIGHT_ORDER, MetricDelta, PANEL_ICON, RARE_DAYS, RECEIPTS_DAYS, STATUS_DAYS,
  type DayRow, type Light, type Panel,
} from './AnalyticsStatusTab';

const WEIGHT: Record<Light, number> = { red: 40, orange: 20, yellow: 8, lime: 3, green: 0, grey: 0 };
// Веса разделов (Алексей 25.10 #2400: «веса как будто не слишком хорошо стоят», редактируемые для группы).
// То, что прямо видит гость и что даёт выручку, — тяжелее; справочное — легче; «Чеки» — справка без
// оценки, в рейтинг не идут. Группа может переопределить (ops_tasks StatusWeights).
const DEFAULT_SECTION_WEIGHT: Record<string, number> = {
  layout: 3, queue: 3, tables: 2, tasks: 2, arrival: 1, speech: 2, reviews: 2, visits: 1, receipts: 0, returns: 1, hours: 1,
};
type Weights = Record<string, number>;
const wOf = (w: Weights, key: string) => w[key] ?? DEFAULT_SECTION_WEIGHT[key] ?? 1;

type ZoneCard = { zoneId: number; name: string; panels: Panel[]; worst: Light; score: number; trend: number | null; daysWithData: number };

function toRows(z: StatusAllZone | undefined): DayRow[] {
  return (z?.days ?? []).map((d) => ({ day: d.day, data: d.findings }));
}

/** Кружок филиала — по среднему уровню разделов, а не по худшему (Алексей 25.10 #2309/#2310:
 *  «почти всё зелёное или жёлтое, а кружок жёлтый», «идеала то никогда не будет»). Красный —
 *  только при двух красных разделах или когда в среднем всё плохо. */
const LIGHT_POINTS: Record<Light, number> = { red: 4, orange: 3, yellow: 2, lime: 1, green: 0, grey: 0 };
function zoneLight(panels: Panel[], w: Weights = {}): Light {
  const lit = panels.filter((p) => p.light !== 'grey' && wOf(w, p.key) > 0);
  if (!lit.length) return 'grey';
  const total = lit.reduce((s, p) => s + wOf(w, p.key), 0);
  const avg = lit.reduce((s, p) => s + LIGHT_POINTS[p.light] * wOf(w, p.key), 0) / total;
  const reds = lit.filter((p) => p.light === 'red').length;
  return reds >= 2 || avg >= 3 ? 'red' : avg >= 2.4 ? 'orange' : avg >= 1.8 ? 'yellow' : avg >= 0.8 ? 'lime' : 'green';
}

const weighted = (panels: Panel[], w: Weights) => panels.reduce((s, p) => s + WEIGHT[p.light] * wOf(w, p.key), 0);

function buildCard(z: StatusAllZone, prev: StatusAllZone | undefined, extra: Panel[], w: Weights): ZoneCard {
  const cur = toRows(z); const pr = toRows(prev);
  // Чек-листы: в «все филиалы» реестр функций не грузим — считаем включёнными, если есть задачи.
  const tasksOn = cur.some((r) => (r.data?.tasks?.total ?? 0) > 0) || pr.some((r) => (r.data?.tasks?.total ?? 0) > 0);
  const panels = [aggLayout(cur, pr, [], []), aggQueue(cur, pr), aggTables(cur, pr, [], []), aggTasks(cur, pr, tasksOn), ...extra]
    .filter((p): p is Panel => p != null);
  const score = weighted(panels, w);
  // Предыдущее окно — те же панели снимков по prev как «текущему», без своей динамики.
  const prevPanels = pr.length ? [aggLayout(pr, [], [], []), aggQueue(pr, []), aggTables(pr, [], [], []), aggTasks(pr, [], tasksOn)].filter((p): p is Panel => p != null) : [];
  // Тренд филиала — только по разделам снимков: у них есть то же прошлое окно.
  const snapScore = weighted(panels.filter((p) => SNAPSHOT_KEYS.has(p.key)), w);
  const trend = pr.length ? snapScore - weighted(prevPanels, w) : null;
  return { zoneId: z.zone_id, name: z.name, panels, worst: zoneLight(panels, w), score, trend, daysWithData: cur.filter((r) => r.data && !r.data.closed).length };
}
const SNAPSHOT_KEYS = new Set(['layout', 'queue', 'tables', 'tasks']);

/** Разделы не из снимков — по филиалу те же ручки, что у его Статуса; по 4 филиала разом. */
async function loadExtras(zoneId: number): Promise<Panel[]> {
  const zid = String(zoneId);
  const f = (await getZoneFeatures(zid).catch(() => null))?.data ?? {};
  const soft = <T,>(p: Promise<T>) => p.catch(() => undefined);
  const rcOn = !!(f.receipts?.enabled && f.receipts.has_content);
  const [arrC, arrP, spC, spP, rvC, rvP, vis, hrs, rcC, rcP] = await Promise.all([
    soft(getStaffArrival(zid, isoDaysAgo(STATUS_DAYS), isoDaysAgo(1)).then((r) => r.data)),
    soft(getStaffArrival(zid, isoDaysAgo(STATUS_DAYS * 2), isoDaysAgo(STATUS_DAYS + 1)).then((r) => r.data)),
    f.speech?.enabled ? soft(getConversationsOverview(zoneId, isoDaysAgo(STATUS_DAYS), isoDaysAgo(1))) : undefined,
    f.speech?.enabled ? soft(getConversationsOverview(zoneId, isoDaysAgo(STATUS_DAYS * 2), isoDaysAgo(STATUS_DAYS + 1))) : undefined,
    f.reviews?.enabled ? soft(getReviewsStats(zid, isoDaysAgo(RARE_DAYS), isoDaysAgo(0)).then((r) => r.data)) : undefined,
    f.reviews?.enabled ? soft(getReviewsStats(zid, isoDaysAgo(RARE_DAYS * 2), isoDaysAgo(RARE_DAYS + 1)).then((r) => r.data)) : undefined,
    f.visits?.enabled && f.visits.has_content ? soft(listZoneVisits(zid, { from: isoDaysAgo(RARE_DAYS), to: isoDaysAgo(0) }).then((r) => r.data.results)) : undefined,
    soft(getHoursChecks(zid).then((r) => r.data)),
    rcOn ? soft(getReceiptsSummary(zid, isoDaysAgo(RECEIPTS_DAYS), isoDaysAgo(1)).then((r) => r.data)) : undefined,
    rcOn ? soft(getReceiptsSummary(zid, isoDaysAgo(RECEIPTS_DAYS * 2), isoDaysAgo(RECEIPTS_DAYS + 1)).then((r) => r.data)) : undefined,
  ]);
  const arrival = aggArrival(arrC, arrP);
  // «Данных о приходе нет» без настроек — не подключено, а не серая ячейка.
  return [arrival && !(arrival.light === 'grey' && arrival.settings) ? arrival : null, aggSpeech(spC, spP), aggReviews(rvC, rvP), aggVisits(vis, null, false),
    aggHours(hrs), rcOn ? aggReceipts(rcC, rcP) : null, rcOn ? aggReturns(rcC, rcP) : null]
    .filter((p): p is Panel => p != null);
}

async function loadAllExtras(ids: number[]): Promise<Record<number, Panel[]>> {
  const out: Record<number, Panel[]> = {};
  let i = 0;
  const worker = async () => { while (i < ids.length) { const id = ids[i++]; out[id] = await loadExtras(id).catch(() => []); } };
  await Promise.all(Array.from({ length: Math.min(4, ids.length) }, worker));
  return out;
}

export default function StatusAllPage() {
  const { t } = useT();
  // Подзаголовок тот же, что у статуса филиала, — не меняется на ходу (#2515 хайку-тест).
  usePageTitle(t('status.title'), t('status.subtitle'));
  const cur = useSWR(['analytics:status:all', STATUS_DAYS, 1], () => getStatusAll(STATUS_DAYS, 1).then((r) => r.data), { revalidateOnFocus: false });
  const prev = useSWR(['analytics:status:all', STATUS_DAYS, STATUS_DAYS + 1], () => getStatusAll(STATUS_DAYS, STATUS_DAYS + 1).then((r) => r.data), { revalidateOnFocus: false });
  // Группа филиалов в списке филиалов (#1969) — только её филиалы.
  const scope = useBranchScope();
  const zones = useMemo(() => (cur.data?.zones ?? []).filter((z) => !scope.zoneIds || scope.zoneIds.has(String(z.zone_id))), [cur.data, scope.zoneIds]);
  const ids = zones.filter((z) => z.days.some((d) => d.findings && !d.findings.closed)).map((z) => z.zone_id);
  const extras = useSWR(ids.length ? ['status:all:extras', ids.join(',')] : null, () => loadAllExtras(ids), { revalidateOnFocus: false, dedupingInterval: 5 * 60_000 });
  const wq = useSWR(scope.groupId ? ['status:weights', scope.groupId] : null, () => getStatusWeights(scope.groupId!).then((r) => r.data).catch(() => undefined), { revalidateOnFocus: false });
  const weights = useMemo(() => wq.data?.weights ?? {}, [wq.data]);
  const cards = useMemo(() => {
    if (!cur.data) return null;
    const prevBy = new Map((prev.data?.zones ?? []).map((z) => [z.zone_id, z]));
    return zones.map((z) => buildCard(z, prevBy.get(z.zone_id), extras.data?.[z.zone_id] ?? [], weights))
      .sort((a, b) => LIGHT_ORDER[a.worst] - LIGHT_ORDER[b.worst] || b.score - a.score || a.name.localeCompare(b.name, 'ru'));
  }, [cur.data, prev.data, zones, extras.data, weights]);

  if (cur.error && !cur.data) return <ServerStatusNotice error={cur.error} />;
  if (!cards) return <StatusAllSkeleton />;
  const live = cards.filter((c) => c.daysWithData > 0);
  // Колонка — только если раздел есть хоть у одного филиала; остальные — внизу «можно подключить»
  // (Алексей 25.10 #2311: «речь ни у одного спота Игоря не подключена — колонки не должно быть»).
  const has = (key: string) => live.some((c) => c.panels.some((p) => p.key === key));
  const shown = SECTIONS.filter((sec) => has(sec.key) || (extras.isLoading && !SNAPSHOT_KEYS.has(sec.key)));
  const hidden = SECTIONS.filter((sec) => !shown.includes(sec));
  const problems = live.filter((c) => c.worst === 'red' || c.worst === 'orange').length;
  return (
    // Без max-w: таблица разделов широкая и обрезалась при свободном месте по бокам (#2504).
    <div className="space-y-3 p-4 md:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold">{scope.groupName ?? 'Все филиалы'}</h2>
          <span className="rounded-full bg-muted px-1.5 py-px text-[11px] font-medium tabular-nums text-muted-foreground">{live.length}</span>
          {problems > 0 && <span className="text-xs text-muted-foreground">· требуют внимания {problems}</span>}
        </div>
        <span className="text-xs text-muted-foreground">
          {cur.data?.from} — {cur.data?.to} · к предыдущим {STATUS_DAYS} дням · отзывы и визиты за {RARE_DAYS} дн., финансы за {RECEIPTS_DAYS} дн.{extras.isLoading && ' · собираем приход, речь, отзывы…'}
        </span>
      </div>
      {live.length <= 3 ? <SideBySide cards={live} loading={extras.isLoading} sections={shown} /> : <Matrix cards={live} loading={extras.isLoading} sections={shown} weights={weights}
        weightsFoot={<WeightsRow weights={weights} canEdit={!!wq.data?.can_edit}
          onSave={scope.groupId ? async (w) => { const r = await putStatusWeights(scope.groupId!, w); await wq.mutate(r.data, { revalidate: false }); } : undefined} />} />}
      <CanAdd sections={hidden} />
      {cards.some((c) => c.daysWithData === 0) && (
        <div className="text-xs text-muted-foreground">
          Без снимков за период: {cards.filter((c) => c.daysWithData === 0).map((c) => c.name).join(', ')}
        </div>
      )}
    </div>
  );
}

// ── Таблица (Алексей 22.10 #1969, 24.10 #2284) ────────────────────────────────
// Строки — филиалы (сначала проблемные), столбцы — разделы с иконкой как на карточках
// филиала; в ячейке — спокойный чип уровня с цифрой и стрелкой динамики. Клик раскрывает
// под строкой сводку раздела этого филиала.

// `off`/`setup` — неподключённый раздел нажимается и «рекламирует» функцию (Алексей 23.10, #2065).
const SECTIONS: { key: string; title: string; short?: string; off: string; setup: string }[] = [
  { key: 'layout', title: 'Выкладка', off: 'Выкладка не подключена', setup: '/management/zoning' },
  { key: 'queue', title: 'Очередь', off: 'Очереди не подключены', setup: '/management/queues-staff' },
  { key: 'tables', title: 'Столы', off: 'Чистота столов не подключена', setup: '/management/zoning' },
  { key: 'tasks', title: 'Чек-листы', off: 'Чек-листы не подключены', setup: '/management/checklists' },
  { key: 'arrival', title: 'Приход', off: 'Приход к открытию не подключён', setup: '/management/queues-staff' },
  { key: 'speech', title: 'Речь', off: 'Общение с гостями не подключено', setup: '/management/speech' },
  { key: 'reviews', title: 'Отзывы', off: 'Отзывы не подключены', setup: '/management/reviews' },
  { key: 'visits', title: 'Визиты', off: 'Визиты не подключены', setup: '/management/visits' },
  // Финансы колонкой, а не отдельным блоком (#2510): выручка в день · средний чек, раскрытие — плитки.
  { key: 'receipts', title: 'Финансы', off: 'Чеки не подключены', setup: '/management/receipts' },
  { key: 'returns', title: 'Возвраты', off: 'Чеки не подключены', setup: '/management/receipts' },
  { key: 'hours', title: 'Часы работы', short: 'Часы', off: 'Часы работы не сверяются', setup: '/management/general' },
];
const SNAP_SECTION = SNAPSHOT_KEYS;
// За сколько дней считается колонка (Алексей 28.10 #3177: «за сколько дней статистика по какому из
// параметров — в хедер»); сравнение — с таким же отрезком перед ним (#3178).
const PERIOD_DAYS: Record<string, number | null> = { reviews: RARE_DAYS, visits: RARE_DAYS, receipts: RECEIPTS_DAYS, returns: RECEIPTS_DAYS, hours: null };
const periodDays = (key: string) => (key in PERIOD_DAYS ? PERIOD_DAYS[key] : STATUS_DAYS);
const periodLabel = (key: string) => { const n = periodDays(key); return n == null ? 'сейчас' : `${n} дн.`; };

function NotConnected({ c, sec }: { c: ZoneCard; sec: (typeof SECTIONS)[number] }) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <span className="font-medium">{c.name}</span>
      <span className="text-muted-foreground">{sec.off}</span>
      <Link to={`${sec.setup}?zone=${c.zoneId}`} className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}>
        <Settings2 className="size-4" />Настроить
      </Link>
    </div>
  );
}

/** Динамика филиала целиком — стрелкой рядом с названием, не отдельной колонкой (#2065). */
function ZoneTrend({ c }: { c: ZoneCard }) {
  if (!c.trend) return null;
  const better = c.trend < 0;
  return (
    <Tooltip content={`${better ? 'Лучше' : 'Хуже'}, чем за предыдущие ${STATUS_DAYS} дней`} side="top">
      <span className={cn('inline-flex shrink-0', better ? 'text-success' : 'text-destructive')}>
        {better ? <ArrowDownRight className="size-4" /> : <ArrowUpRight className="size-4" />}
      </span>
    </Tooltip>
  );
}

function Dot({ light, className }: { light: Light; className?: string }) {
  return <Tooltip content={LIGHT_LABEL[light]}><span className={cn('inline-block size-2 shrink-0 rounded-full', LIGHT_DOT[light], className)} /></Tooltip>;
}

/** Динамика шевронами (Алексей 04.11 #4054): 1–4 по силе изменения, вверх/вниз — фактическое направление,
 *  цвет — лучше/хуже; без изменений — волна, не прочерк. */
function TrendMark({ d }: { d: NonNullable<Panel['delta']> }) {
  if (d.better == null) {
    return (
      <svg viewBox="0 0 12 8" className="h-2 w-3 shrink-0 text-muted-foreground" aria-label="без изменений">
        <path d="M1 5 Q3.5 1.5 6 4 T11 3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  const n = d.rel < 0.1 ? 1 : d.rel < 0.25 ? 2 : d.rel < 0.5 ? 3 : 4;
  const h = 4 + (n - 1) * 3;
  return (
    <svg viewBox={`0 0 10 ${h + 2}`} style={{ height: (h + 2) * 1.25, width: 12.5 }}
      className={cn('shrink-0', d.better ? 'text-success' : 'text-destructive', !d.up && 'rotate-180')}
      aria-label={`${d.better ? 'лучше' : 'хуже'}, ${n} из 4`}>
      {Array.from({ length: n }, (_, i) => (
        <path key={i} d={`M1.5 ${5 + i * 3} L5 ${1.5 + i * 3} L8.5 ${5 + i * 3}`} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </svg>
  );
}

/** Подсказка ячейки — белая карточка: что за число, насколько изменилось, тезисно в чём проблемы (#4054).
 *  Период — в шапке таблицы, здесь не повторяем. */
function CellTip({ p }: { p: Panel }) {
  const d = p.delta;
  const value = p.cell ?? d?.value ?? p.value?.value;
  const points = (p.remarks.length ? p.remarks.map((r) => ({ light: r.light, text: r.text }))
    : p.headline.split(' · ').map((text) => ({ light: p.light, text }))).slice(0, 4);
  return (
    <div className="grid gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium">{p.title}</span>
        {value && <span className="tabular-nums font-semibold">{value}{p.value?.what ? <span className="font-normal text-muted-foreground"> {p.value.what}</span> : null}</span>}
      </div>
      {d && (
        <div className={cn('flex items-center gap-1.5', d.better == null ? 'text-muted-foreground' : d.better ? 'text-success' : 'text-destructive')}>
          <TrendMark d={d} />
          <span>{d.better == null ? 'Без изменений' : `${d.better ? 'Лучше' : 'Хуже'}: ${d.text}${d.change ? ` (${d.change})` : ''}`}</span>
        </div>
      )}
      {points.length > 0 && (
        <ul className="grid gap-1 border-t border-border pt-1.5">
          {points.map((x, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', LIGHT_DOT[x.light])} />
              <span>{x.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Ячейка: чип уровня с цифрой (или подписью уровня) и стрелкой динамики; подробности — в подсказке. */
function Cell({ p, sec, loading, active, onClick }: { p: Panel | undefined; sec: (typeof SECTIONS)[number]; loading: boolean; active: boolean; onClick: () => void }) {
  // Скелетон уже итогового чипа — таблица по мере загрузки не сужается (#2505).
  if (!p && loading && !SNAP_SECTION.has(sec.key)) return <span className="block h-7 w-10 animate-pulse rounded-md bg-muted" />;
  const d = p?.delta;
  return (
    <Tooltip content={p ? <CellTip p={p} /> : sec.off} side="top"
      className={p ? 'max-w-sm rounded-lg border border-border bg-popover p-3 text-xs text-popover-foreground shadow-lg' : undefined}>
      <button type="button" onClick={onClick}
        className={cn('inline-flex h-7 max-w-full items-center whitespace-nowrap gap-1.5 rounded-md px-2 text-xs font-medium transition-[box-shadow,opacity] hover:ring-1 hover:ring-foreground/20',
          p ? LIGHT_CHIP[p.light] : 'min-w-9 justify-center border border-dashed border-border text-muted-foreground/70', active && 'ring-2 ring-primary/50')}>
        {p ? (
          <>
            {/* Всегда число (#2325: «пролезает „Небольшая проблема“, а не число»); числа нет — точка уровня. */}
            {!(p.cell ?? d?.value ?? p.value?.value) && <span className={cn('size-2 shrink-0 rounded-full', LIGHT_DOT[p.light])} />}
            {(p.cell ?? d?.value ?? p.value?.value) && <span className="truncate tabular-nums">{p.cell ?? d?.value ?? p.value?.value}</span>}
            {d && <TrendMark d={d} />}
          </>
        ) : <span aria-label="не подключено">—</span>}
      </button>
    </Tooltip>
  );
}

/** Раскрытая ячейка — ровно та панель, что на статусе филиала при раскрытии (Алексей 25.10 #2325:
 *  «по клику на ячейку как и всегда должны быть фото… то же самое, что по одному филиалу»). */
function ZonePanel({ c, panelKey, onClose }: { c: ZoneCard; panelKey: string; onClose: () => void }) {
  const zid = String(c.zoneId);
  const { features } = useZoneFeatures(zid);
  if (!features) return <div className="h-24 animate-pulse rounded-lg bg-muted" />;
  const sf = {
    tasks: !!(features.checklists?.enabled && features.checklists.has_content),
    speech: !!features.speech?.enabled,
    reviews: !!features.reviews?.enabled,
    visits: !!(features.visits?.enabled && features.visits.has_content),
    receipts: !!(features.receipts?.enabled && features.receipts.has_content),
  };
  // Разворот ячейки — сама панель без своей рамки (#2395): филиал в заголовке, шеврон сворачивает.
  return <AnalyticsStatusTab zoneId={zid} features={sf} onlyKey={panelKey}
    embed={{ zoneName: c.name, zoneLink: `/management/status?zone=${zid}`, onCollapse: onClose }} />;
}

function SectionHead({ sec }: { sec: (typeof SECTIONS)[number] }) {
  const Icon = PANEL_ICON[sec.key] ?? BarChart3;
  return (
    <span className="inline-flex flex-col items-start leading-tight">
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Icon className="size-3.5" strokeWidth={1.75} />{sec.short ?? sec.title}</span>
      <span className="pl-5 text-[10px] font-normal text-muted-foreground/80">{periodLabel(sec.key)}</span>
    </span>
  );
}

function ZoneName({ c, light }: { c: ZoneCard; light?: Light }) {
  return (
    <div className="flex items-center gap-1.5">
      {/* Без перехода по названию (#2511): в таблице переходы только кнопками. */}
      <span className="flex min-w-0 items-center gap-2 font-medium text-foreground">
        <Dot light={light ?? c.worst} /><span className="truncate">{c.name}</span>
      </span>
      <ZoneTrend c={c} />
    </div>
  );
}

type Section = (typeof SECTIONS)[number];

/** Порядок ячейки при сортировке по колонке: сначала уровень, внутри — число из ячейки. */
function cellRank(c: ZoneCard, key: string): [number, number] {
  const p = c.panels.find((x) => x.key === key);
  if (!p) return [99, 0];
  const n = parseFloat((p.delta?.value ?? p.value?.value ?? '').replace(',', '.').replace(/[^\d.-]/g, ''));
  return [LIGHT_ORDER[p.light], Number.isFinite(n) ? n : 0];
}

function SortIcon({ dir }: { dir: 'asc' | 'desc' | null }) {
  const I = dir === 'asc' ? ChevronUp : dir === 'desc' ? ChevronDown : ChevronsUpDown;
  return <I className={cn('size-3.5', dir ? 'text-foreground' : 'opacity-40')} />;
}

type ZoneMode = 'overall' | 'common' | 'alpha';
const ZONE_MODES: { mode: ZoneMode; label: string; hint: string }[] = [
  { mode: 'common', label: 'Рейтинг по общим данным', hint: 'Только разделы, которые есть у всех филиалов, — кружки перекрашиваются' },
  { mode: 'overall', label: 'Общий рейтинг', hint: 'По всем разделам каждого филиала' },
  { mode: 'alpha', label: 'По алфавиту', hint: '' },
];

function Matrix({ cards, loading, sections: allSections, weights, weightsFoot }: { cards: ZoneCard[]; loading: boolean; sections: Section[]; weights: Weights; weightsFoot?: React.ReactElement<WeightsRowProps> }) {
  const [open, setOpen] = useState<{ zone: number; key: string } | null>(null);
  // Сортировка (#2315/#2325): по умолчанию — по кружку филиала (общий рейтинг), это видно в шапке.
  // Колонка раздела: клик — сначала худшие, ещё клик — лучшие, третий — снова по филиалу.
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'zone', dir: 'asc' });
  const [zoneMode, setZoneMode] = useState<ZoneMode>('overall');
  const [menu, setMenu] = useState(false);
  // Раскрытая ячейка — по ширине видимой области, а не всей прокручиваемой таблицы:
  // иначе правый край панели («Статус филиала») уезжал за край (#2325).
  const wrapRef = useRef<HTMLDivElement>(null);
  const [wrapW, setWrapW] = useState<number>();
  useEffect(() => {
    const el = wrapRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setWrapW(el.clientWidth));
    ro.observe(el); return () => ro.disconnect();
  }, []);
  // «Только общие данные» (#2325): у Остоженки данных больше всех — по общему рейтингу она хуже
  // спота с одним мелким разделом; здесь сравниваем только разделы, которые есть у всех.
  const common = useMemo(() => {
    const keys = new Set(cards[0]?.panels.map((p) => p.key) ?? []);
    for (const c of cards) for (const k of [...keys]) if (!c.panels.some((p) => p.key === k)) keys.delete(k);
    return keys;
  }, [cards]);
  const lightOf = (c: ZoneCard) => (zoneMode === 'common' ? zoneLight(c.panels.filter((p) => common.has(p.key)), weights) : c.worst);
  const scoreOf = (c: ZoneCard) => weighted(zoneMode === 'common' ? c.panels.filter((p) => common.has(p.key)) : c.panels, weights);
  // «По общим данным» (#2400): общие колонки — слева, остальные — за чертой.
  const sections = useMemo(() => (zoneMode === 'common'
    ? [...allSections.filter((s) => common.has(s.key)), ...allSections.filter((s) => !common.has(s.key))]
    : allSections), [allSections, zoneMode, common]);
  const divider = zoneMode === 'common' ? sections.find((s) => !common.has(s.key))?.key : undefined;
  const rows = useMemo(() => {
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...cards].sort((a, b) => {
      if (sort.key === 'zone') {
        if (zoneMode === 'alpha') return a.name.localeCompare(b.name, 'ru');
        return LIGHT_ORDER[lightOf(a)] - LIGHT_ORDER[lightOf(b)] || scoreOf(b) - scoreOf(a) || a.name.localeCompare(b.name, 'ru');
      }
      const [la, na] = cellRank(a, sort.key); const [lb, nb] = cellRank(b, sort.key);
      return sign * (la - lb) || sign * (nb - na) || a.name.localeCompare(b.name, 'ru');
    });
  }, [cards, sort, zoneMode, common]);
  const toggle = (key: string) => setSort((s) => (s.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : { key: 'zone', dir: 'asc' }));
  const dirOf = (key: string) => (sort.key === key ? sort.dir : null);
  return (
    <div ref={wrapRef} className="overflow-x-auto rounded-lg border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-card text-xs text-muted-foreground">
            <th className="sticky left-0 z-20 bg-card px-3 py-2.5 text-left font-medium">
              <div className="relative inline-block">
                <button type="button" onClick={() => setMenu((m) => !m)} className={cn('inline-flex items-center gap-1 hover:text-foreground', sort.key === 'zone' && 'text-foreground')}>
                  Филиал
                  <span className="font-normal text-muted-foreground">· {ZONE_MODES.find((m) => m.mode === zoneMode)!.label.toLowerCase()}</span>
                  <SortIcon dir={sort.key === 'zone' ? 'asc' : null} />
                </button>
                {menu && (
                  <div className="absolute left-0 top-full z-30 mt-1 w-72 rounded-lg border bg-popover p-1 text-sm text-foreground shadow-lg" onMouseLeave={() => setMenu(false)}>
                    {ZONE_MODES.map((m) => (
                      <button key={m.mode} type="button" onClick={() => { setZoneMode(m.mode); setSort({ key: 'zone', dir: 'asc' }); setMenu(false); }}
                        className={cn('flex w-full flex-col items-start rounded-md px-2.5 py-1.5 text-left hover:bg-muted', zoneMode === m.mode && sort.key === 'zone' && 'bg-muted')}>
                        <span className="font-medium">{m.label}</span>
                        {m.hint && <span className="text-xs font-normal text-muted-foreground">{m.hint}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </th>
            {sections.map((sec) => (
              <th key={sec.key} className={cn('px-2 py-2.5 text-left font-medium', sec.key === divider && 'border-l-2 border-border')}>
                <Tooltip content="Сортировать: сначала проблемные"><button type="button" onClick={() => toggle(sec.key)} className="inline-flex items-center gap-1 hover:text-foreground" aria-label="Сортировать: сначала проблемные">
                  <SectionHead sec={sec} /><SortIcon dir={dirOf(sec.key)} />
                </button></Tooltip>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const openPanel = open?.zone === c.zoneId ? c.panels.find((p) => p.key === open.key) ?? null : null;
            const openOff = open?.zone === c.zoneId && !openPanel ? sections.find((x) => x.key === open.key) ?? null : null;
            return (
              <React.Fragment key={c.zoneId}>
                <tr className="border-b last:border-b-0 hover:bg-muted/20">
                  <td className="sticky left-0 z-10 max-w-56 bg-card px-3 py-2">
                    <ZoneName c={c} light={lightOf(c)} />
                  </td>
                  {sections.map((sec) => {
                    const active = open?.zone === c.zoneId && open.key === sec.key;
                    return (
                      <td key={sec.key} className={cn('px-2 py-2', sec.key === divider && 'border-l-2 border-border')}>
                        <Cell p={c.panels.find((x) => x.key === sec.key)} sec={sec} loading={loading} active={active}
                          onClick={() => setOpen(active ? null : { zone: c.zoneId, key: sec.key })} />
                      </td>
                    );
                  })}
                </tr>
                {(openPanel || openOff) && (
                  <tr className="border-b bg-muted/20">
                    <td colSpan={sections.length + 1} className="p-0">
                      <div className="sticky left-0 px-4 py-4" style={{ width: wrapW }}>{openPanel ? <ZonePanel c={c} panelKey={openPanel.key} onClose={() => setOpen(null)} /> : openOff && <NotConnected c={c} sec={openOff} />}</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        {/* Веса — строкой-футером под своими колонками (Алексей 26.10 #2513). */}
        {weightsFoot && React.cloneElement(weightsFoot, { sections, divider })}
      </table>
    </div>
  );
}

/** Веса разделов — серым под таблицей, свёрнуто (#2400). Администратор группы правит и сохраняет. */
type WeightsRowProps = { weights: Weights; canEdit: boolean; onSave?: (w: Weights) => Promise<void>; sections?: Section[]; divider?: string };
function WeightsRow({ weights, canEdit, onSave, sections = [], divider }: WeightsRowProps) {
  // Свёрнута по умолчанию (#2523): веса нужны редко.
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Weights | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const cur = draft ?? weights;
  const dirty = draft != null && sections.some((s) => wOf(draft, s.key) !== wOf(weights, s.key));
  const set = (k: string, v: number) => setDraft({ ...cur, [k]: Math.max(0, Math.min(5, v)) });
  const save = async () => {
    if (!onSave || !draft) return;
    setBusy(true); setErr('');
    try { await onSave(Object.fromEntries(sections.map((s) => [s.key, wOf(draft, s.key)]))); setDraft(null); }
    catch { setErr('Не сохранилось'); }
    finally { setBusy(false); }
  };
  return (
    <tfoot>
      <tr className="border-t text-xs text-muted-foreground">
        <td className="sticky left-0 z-10 bg-card px-3 py-2">
          <span className="flex items-center gap-2 whitespace-nowrap">
            <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 hover:text-foreground">
              <ChevronDown className={cn('size-3.5 transition-transform', !open && '-rotate-90')} />Вес в рейтинге
            </button>
            {dirty && <button type="button" disabled={busy} onClick={save} className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'h-6 px-2 text-xs')}>{busy ? '…' : 'Сохранить'}</button>}
            {dirty && <button type="button" onClick={() => setDraft(null)} className="hover:text-foreground">Отменить</button>}
            {err && <span className="text-destructive">{err}</span>}
          </span>
        </td>
        {sections.map((s) => (
          <td key={s.key} className={cn('px-2 py-2', s.key === divider && 'border-l-2 border-border')}>
            {!open ? null : canEdit ? (
              <span className="inline-flex items-center rounded border">
                <button type="button" onClick={() => set(s.key, wOf(cur, s.key) - 1)} className="px-1.5 hover:text-foreground" aria-label="Меньше">−</button>
                <span className="w-4 text-center tabular-nums text-foreground">{wOf(cur, s.key)}</span>
                <button type="button" onClick={() => set(s.key, wOf(cur, s.key) + 1)} className="px-1.5 hover:text-foreground" aria-label="Больше">+</button>
              </span>
            ) : <span className="tabular-nums">{wOf(cur, s.key)}</span>}
          </td>
        ))}
      </tr>
    </tfoot>
  );
}

/** Разделы, которых нет ни у одного филиала группы, — как «Можно подключить» на статусе филиала. */
function CanAdd({ sections }: { sections: Section[] }) {
  if (!sections.length) return null;
  return (
    <section className="space-y-2 pt-3">
      <div className="text-sm font-semibold">Можно подключить</div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sections.map((sec) => {
          const Icon = PANEL_ICON[sec.key] ?? BarChart3;
          return (
            <Link key={sec.key} to={sec.setup} className="group flex items-center gap-3 rounded-lg border border-dashed border-border bg-card p-3 transition-colors hover:border-primary/50 hover:bg-muted/40">
              <Icon className="size-5 shrink-0 text-muted-foreground group-hover:text-primary" />
              <span className="min-w-0 flex-1 text-sm font-medium">{sec.title}<span className="block text-xs font-normal text-muted-foreground">{sec.off}</span></span>
              <span className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0 text-xs')}>Настроить</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// 2–3 филиала: столбцы — филиалы, строки — разделы; сравнивать рядом. Клик раскрывает
// подробности под таблицей.
function SideBySide({ cards, loading, sections }: { cards: ZoneCard[]; loading: boolean; sections: Section[] }) {
  const [open, setOpen] = useState<{ zone: number; key: string } | null>(null);
  const openCard = open ? cards.find((c) => c.zoneId === open.zone) ?? null : null;
  const openPanel = openCard?.panels.find((p) => p.key === open?.key) ?? null;
  const openOff = openCard && !openPanel ? sections.find((x) => x.key === open?.key) ?? null : null;
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full table-fixed text-sm">
          <thead>
            <tr className="border-b bg-card text-xs text-muted-foreground">
              <th className="w-36 px-3 py-2.5 text-left font-medium" />
              {cards.map((c) => <th key={c.zoneId} className="px-3 py-2.5 text-left text-sm"><ZoneName c={c} /></th>)}
            </tr>
          </thead>
          <tbody>
            {sections.map((sec) => (
              <tr key={sec.key} className="border-b last:border-b-0">
                <td className="px-3 py-2 text-xs text-muted-foreground"><SectionHead sec={sec} /></td>
                {cards.map((c) => {
                  const p = c.panels.find((x) => x.key === sec.key);
                  const active = open?.zone === c.zoneId && open.key === sec.key;
                  return (
                    <td key={c.zoneId} className="px-3 py-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <Cell p={p} sec={sec} loading={loading} active={active} onClick={() => setOpen(active ? null : { zone: c.zoneId, key: sec.key })} />
                        {p && <span className="hidden min-w-0 truncate text-xs text-muted-foreground lg:inline">{p.delta?.what ?? ''}</span>}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openCard && openPanel && <div className="rounded-lg border bg-card p-4"><ZonePanel c={openCard} panelKey={openPanel.key} onClose={() => setOpen(null)} /></div>}
      {openCard && openOff && <div className="rounded-lg border bg-card p-4"><NotConnected c={openCard} sec={openOff} /></div>}
    </div>
  );
}

/** Загрузка — скелетон в форме таблицы филиалов, а не текст в углу (Алексей 04.11 #4052). */
function StatusAllSkeleton() {
  return (
    <div className="space-y-3 p-4 md:p-6" aria-busy="true" aria-label="Загрузка">
      <div className="h-6 w-56 animate-pulse rounded bg-muted" />
      <div className="overflow-hidden rounded-[var(--radius-lg)] border border-border bg-card">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="flex items-center gap-4 border-b border-border px-4 py-3 last:border-b-0">
            <div className="size-3 shrink-0 animate-pulse rounded-full bg-muted" />
            <div className="h-4 w-40 animate-pulse rounded bg-muted" />
            <div className="ml-auto flex gap-3">
              {Array.from({ length: 5 }, (_, j) => <div key={j} className="h-4 w-14 animate-pulse rounded bg-muted" />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
